/// <reference path="../../../typings/abTest/abTesting.d.ts" />

//core
import React, { useContext, useEffect, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";

// hooks
import useAPI from "../hooks/useAPI";
import useSettingsBuilder from "../hooks/useSettingsBuilder";
import { QuoteGroupContext } from "../providers/RootContextProvider";
import { createOrSetClientsAPI } from "../api/api";
import { getProductTypeAsString } from "../../common/shared/enumHelpers";

// components
import Layout from "../components/Layout/Layout";
import BaseProductForm from "../components/Products/BaseProductForm";
import SettingNavigation from "../components/SettingNavigation/SettingNavigation";
import Alert from "../../common/components/Alert/Alert";
import ClientSettingsStreamlineSection from "../components/StreamlineSettings/ClientSettingsStreamlineSection";

// interfaces
import ProductType, {
    ClientProductTypes,
    ClientProductTypesList
} from "../../common/interfaces/enums/ProductType";
import { Severity } from "../../common/interfaces/enums/Severity";

// styles
import cx from "classnames";
import { retrieveGroupMapper } from "../api/mapper/mapper";
import { addQuoteToGroupAPI, createQuoteAPI } from "../api/endpoints/ApiQuotes";

const StreamlineSettingsPage: React.FC = () => {
    const history = useHistory();
    const { selectors, actions } = useContext(QuoteGroupContext);
    const { defaultQuoteClient } = useSettingsBuilder();

    const { currentProduct } = useParams<{
        currentProduct: ClientProductTypes;
    }>();

    const [isLoading, setIsLoading] = useState(false);
    const initialClients = selectors.getClients().length > 0 || isLoading
        ? selectors.getClients()
        : [{ ...defaultQuoteClient[currentProduct] }];

    const showInfoBox = currentProduct === ProductType.Life;

    if (!ClientProductTypesList.includes(currentProduct))
        return <Redirect to="/family" />;

    useEffect(() => {
        abTesting.convertAll(7, "", getProductTypeAsString, 0);
    }, []);

    return (
        <Layout hideMenu progressBarCurrentStage={"build"}>
            <BaseProductForm
                clients={initialClients}
                compareProducts={[currentProduct]}
                validationProducts={[currentProduct]}
                settings={selectors.getAllSettings()}
                onSubmit={async (settings) => {
                    setIsLoading(true);
                    
                    actions.clearQuoteResults();
                    ClientProductTypesList.map((p) => actions.setProductEnabled(p, p === currentProduct));

                    const res = await createOrSetClientsAPI(
                        selectors.getGroupId(),
                        selectors.getClients()
                    );

                    const clients = selectors.getClients()
                        .map((client, index) => {
                            return { ...client, personId: res.clients[index]?.personId ?? 0};
                        })
                        .filter((client) => client.isActive && client.personId > 0);

                    actions.setClients(clients);

                    const mapper = retrieveGroupMapper(currentProduct);
                    const currentSetting = settings.find(s => s.productType === currentProduct);
                    currentSetting.clients.forEach((c, i) => c.personId = clients[i]?.personId ?? 0);

                    const caseId = await createQuoteAPI(mapper, clients, currentSetting);
                    actions.setQuoteConfig({ ...currentSetting, caseId });

                    return addQuoteToGroupAPI(selectors.getGroupId(), caseId)
                        .then(() => {
                            history.push("/results");
                        })
                        .catch(() =>
                            alert(
                                "Something went wrong creating your quotes, please try again."
                            )
                        );

                }}>
                {({ handleSubmit, submitCount, isSubmitting, errors }) => {
                    return (
                        <React.Fragment>
                            <div
                                className={cx(
                                    "gap-pre-4xl layout-flex-column--centered",
                                    { ["gap-3xl"]: !showInfoBox }
                                )}>
                                <h2 className="text-primary text-align--center gap-4xl padding-horizontal-m">
                                    Build your{" "}
                                    {getProductTypeAsString(
                                        currentProduct,
                                        true
                                    ).toLowerCase()}{" "}
                                    cover quote
                                </h2>
                                <ClientSettingsStreamlineSection
                                    currentProduct={currentProduct}
                                    isLoading={isLoading}
                                />
                            </div>
                            {showInfoBox && (
                                <div className="layout-flex-row--justify-centered">
                                    <Alert
                                        severity={Severity.Info}
                                        isFitContent={false}
                                        className="layout-simple-select-alert gap-pre-2xl gap-m">
                                        <span className="text-primary text-bold text-font-size-s text-paragraph">
                                            LifeDirect&apos;s service is FREE and
                                            doesn&apos;t affect the price of your policy
                                        </span>
                                    </Alert>
                                </div>
                            )}
                            <SettingNavigation
                                onClickCallToAction={handleSubmit}
                                isDisabledCallToAction={submitCount > 0 && Object.keys(errors).length > 0}
                                isLoading={isSubmitting}
                                gaAttributes={{
                                    action: "family"
                                }}
                                isEditing={false}
                            />
                        </React.Fragment>
                    );
                }}
            </BaseProductForm>
        </Layout>
    );
};

export default StreamlineSettingsPage;